import {
  Button,
  Group,
  TextInput,
  Input,
  Stack,
  Checkbox,
  Anchor,
  Alert,
  LoadingOverlay,
  Box,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { IMaskInput } from "react-imask";
import { modals } from "@mantine/modals";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { APP_URL } from "../../shared/config";
import * as Yup from "yup";
import { IconAlertCircle, IconCircleCheck } from "@tabler/icons-react";
import {
  type CitadelApiError,
  sendParticipationRequest,
} from "../../shared/api/mail";
import { type AxiosError } from "axios";
import { AmoRequest } from "shared/types/amo";

// ----------------------------------------------------------------------

const schema = Yup.object().shape({
  contact: Yup.object().shape({
    name: Yup.string().required("Имя обязательно"),
    email: Yup.string()
      .email("Неправильный формат почты")
      .required("Электронный адрес обязателен"),
    phone: Yup.string()
      .required("Телефон обязателен")
      .length(18, "Пожалуйста, укажите телефон полностью"),
  }),
  termsOfService: Yup.boolean()
    .isTrue("Вы должны принять наши правила")
    .required(),
});

// ----------------------------------------------------------------------

type Props = {
  target: string;
};

export const Form = observer(function Form({ target }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    validateInputOnChange: true,
    validate: yupResolver(schema),
    initialValues: {
      contact: {
        name: "",
        phone: "",
        email: "",
      },
      termsOfService: false,
    },
  });

  const handleClose = () => modals.closeAll();

  const onSubmit = ({
    termsOfService,
    ...values
  }: typeof form.values): void => {
    setIsLoading(true);
    setIsSuccess(false);
    setError(null);

    const amoRequest: AmoRequest = {
      name: target,
      contact: {
        name: values.contact.name,
        phone: values.contact.phone.replace(/[\s()-]/g, ""),
        email: values.contact.email,
      },
    };

    void sendParticipationRequest(amoRequest)
      .then(() => {
        setIsSuccess(true);
      })
      .catch((err: AxiosError<CitadelApiError>) => {
        if (err.response) {
          setError(err.response.data.errors[0].detail);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    return () => {
      form.reset();
      setIsLoading(false);
      setIsSuccess(false);
      setError(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box w="100%" pos="relative">
      <LoadingOverlay visible={isLoading} overlayBlur={0.8} />
      {isSuccess ? (
        <>
          <Alert
            icon={<IconCircleCheck />}
            title="Заявка успешно отправлена!"
            color="green"
            radius="md"
          >
            В ближайшее время с Вами свяжется наш сотрудник и ответит на все
            Ваши вопросы.
          </Alert>
          <Group position="center" mt="md">
            <Button radius="md" onClick={handleClose}>
              Закрыть окно
            </Button>
          </Group>
        </>
      ) : (
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack my="xl">
            <TextInput
              placeholder="Имя"
              radius={12}
              size="md"
              {...form.getInputProps("contact.name")}
            />
            <Input.Wrapper
              id="participant-form-phone"
              error={form.errors["contact.phone"]}
              size="md"
            >
              <Input
                component={IMaskInput}
                mask="+7 (000) 000-00-00"
                id="participant-form-phone"
                placeholder="Телефон"
                radius={12}
                size="md"
                {...form.getInputProps("contact.phone")}
              />
            </Input.Wrapper>
            <TextInput
              placeholder="Электронная почта"
              radius={12}
              size="md"
              {...form.getInputProps("contact.email")}
            />
            <Checkbox
              mt="md"
              label={
                <>
                  Я согласен с{" "}
                  <Anchor
                    component="a"
                    href={`${APP_URL}/privacy-policy`}
                    target="_blank"
                  >
                    политикой конфиденциальности
                  </Anchor>
                </>
              }
              {...form.getInputProps("termsOfService", { type: "checkbox" })}
            />
          </Stack>

          {error && (
            <Alert
              mt="xl"
              icon={<IconAlertCircle />}
              title="Ошибка отправки данных"
              color="red"
            >
              {error}
            </Alert>
          )}

          <Group position="center" pt="md">
            <Button
              loading={isLoading}
              disabled={!form.isDirty() || !form.isValid()}
              type="submit"
              radius="md"
            >
              Отправить
            </Button>
            <Button
              disabled={isLoading}
              radius="md"
              variant="default"
              onClick={handleClose}
            >
              Отмена
            </Button>
          </Group>
        </form>
      )}
    </Box>
  );
});
